// eslint-disable-next-line import/no-cycle
import {
  Property, StringMap, TemplateParentTypeEnum, TemplateStructureEnum,
} from '@/types';
// eslint-disable-next-line import/no-cycle
import {
  getSectionIdsFromIframe, setActiveSection,
  TemplateEditorState as state, updateSectionsInState,
} from '@/composables/template-editor/TemplateEditor';
// eslint-disable-next-line import/no-cycle
import { asInt } from '@/helpers';
// eslint-disable-next-line import/no-cycle
import createTimedLRUCache from '@/helpers/createTimedLRUCache';
// eslint-disable-next-line import/no-cycle
import { useEditorBackground } from '@/composables/useEditorBackground';
// eslint-disable-next-line import/no-cycle
import {
  TEMPLATE_SECTION_IDENTIFIER,
  TEMPLATE_LINE_IDENTIFIER,
  TEMPLATE_COLUMN_IDENTIFIER,
  TEMPLATE_WIDGET_IDENTIFIER,
  TEMPLATE_STRUCTURE_MAP,
  TEMPLATE_ITEM_IDENTIFIER_PREFIX_MAP,
} from './constants';
// eslint-disable-next-line import/no-cycle
import {
  createNewStylesheet,
  generateUniqStructureId,
  getSectionParent,
  getTemplateIframeDocument, isDisplayTemplate, isEmailTemplate, replaceSelectors,
} from './helpers';

const GLOBAL_CSS = 'spm_styles_to_keep';

/**
 * Check if a mobile stylesheet should be skipped (mobile styles are disabled)
 * @param stylesheet - The stylesheet element to check
 * @returns true if the stylesheet should be skipped
 */
const shouldSkipMobileStylesheet = (stylesheet: HTMLStyleElement): boolean => {
  const stylesType = stylesheet.getAttribute('data-spm-styles') ?? '';
  const isMobileStylesheet = /_mobile$/.test(stylesType);
  const hasMobileDisabledComment = stylesheet.innerHTML.includes('/*data-spmhidemobile');
  return isMobileStylesheet && hasMobileDisabledComment;
};

export const getTemplateSections = (): HTMLElement[] => {
  const template = getTemplateIframeDocument();
  return Array.from(template?.querySelector('#spm_body:not(.spm_facebook)')?.querySelectorAll(TEMPLATE_SECTION_IDENTIFIER) || []);
};

export const getTemplateLines = (): HTMLElement[] => {
  const template = getTemplateIframeDocument();
  return Array.from(template?.querySelectorAll(TEMPLATE_LINE_IDENTIFIER) || []);
};

export const getTemplateColumns = (): HTMLElement[] => {
  const template = getTemplateIframeDocument();
  return Array.from(template?.querySelectorAll(TEMPLATE_COLUMN_IDENTIFIER) || []);
};

export const getTemplateWidgets = (): HTMLElement[] => {
  const template = getTemplateIframeDocument();
  return Array.from(template?.querySelectorAll(TEMPLATE_WIDGET_IDENTIFIER) || []);
};

export const getGlobalCSS = (template: HTMLDocument): CSSStyleSheet | undefined => {
  const { styleSheets } = template;
  return Object.values(styleSheets).find(({ title }: CSSStyleSheet) => title === GLOBAL_CSS);
};

export const getDynamicStylesForActiveSection = (
  sectionId: string | number | null = null,
  mobile = false,
  forceStylesSheet: string | false = false,
): HTMLStyleElement => {
  let styleType: string;

  if (forceStylesSheet) {
    styleType = forceStylesSheet;
  } else if (mobile === true) {
    styleType = 'dynamic_persistent_mobile';
  } else {
    styleType = 'dynamic';
  }

  const activeSection = sectionId ?? state.template?.activeSection;

  return getTemplateIframeDocument().querySelector(
    `[data-spm-styles="${styleType}"][data-spm-section="${activeSection}"]`,
  ) as HTMLStyleElement;
};

export const setActiveSectionFromSelector = (selector: string) => {
  if (!state.template?.activeSection || state.template?.activeSection === -1) {
    const element = getTemplateIframeDocument().querySelector(selector);
    if (element) {
      const section = element.closest('[data-spmelementid]');
      if (section) {
        const sectionId = section.getAttribute('data-spmelementid');
        if (sectionId) {
          setActiveSection(parseInt(sectionId, 10));
        }
      }
    }
  }
};

export const addNewDynamicStyle = (selector: string, value: StringMap, mobile = false, forceStylesSheet: string | false = false) => {
  setActiveSectionFromSelector(selector);
  if (state.template?.activeSection && state.template?.activeSection !== -1) {
    let stylesheet = getDynamicStylesForActiveSection(null, mobile, forceStylesSheet);
    if (!stylesheet && state.template?.activeSection) {
      let styleType: string;
      if (forceStylesSheet) {
        styleType = forceStylesSheet;
      } else if (mobile === true) {
        styleType = 'dynamic_persistent_mobile';
      } else {
        styleType = 'dynamic';
      }
      // Active section has no dynamic styles stylesheet, we create it
      stylesheet = createNewStylesheet(styleType, state.template?.activeSection.toString());
    }

    if (stylesheet) {
      // Skip entire processing if mobile styles are disabled
      if (shouldSkipMobileStylesheet(stylesheet)) {
        return;
      }

      const { sheet } = stylesheet;
      if (sheet) {
        Object.entries(value)
          .forEach(([property, propertyValue]) => {
            if (propertyValue && propertyValue !== '') {
              if (mobile !== true) {
                sheet.insertRule(`${selector} { ${property}: ${propertyValue}; }`, sheet.cssRules.length);
              } else {
                Array.from(sheet.cssRules).forEach((rule: any) => {
                  if (rule.constructor.name === 'CSSMediaRule') {
                    const mediaRule = rule as CSSMediaRule;
                    mediaRule.insertRule(`${selector} { ${property}: ${propertyValue}; }`, mediaRule.cssRules.length);
                  }
                });
              }
            }
          });
        stylesheet.innerHTML = Array.from(sheet.cssRules)
          .map((rule) => rule.cssText)
          .join('\n');
      }
    }
  }
};

export const moveDynamicStyleFromSectionToAnother = (selector: string, sectionIdFrom: string, sectionIdTo: string) => {
  const template = getTemplateIframeDocument();
  if (sectionIdFrom && sectionIdTo) {
    const dynamicStylesFrom = getDynamicStylesForActiveSection(sectionIdFrom);
    const dynamicStylesTo = getDynamicStylesForActiveSection(sectionIdTo);
    if (dynamicStylesFrom && dynamicStylesTo) {
      const { sheet: sheetFrom } = dynamicStylesFrom;
      const { sheet: sheetTo } = dynamicStylesTo;

      if (sheetFrom && sheetTo) {
        let reconstructFrom = false;
        let index = 0;

        const rulesToAdd: string[] = [];

        Object.values(sheetFrom.cssRules).forEach((styleRule: any) => {
          if (typeof styleRule.selectorText === 'string' && styleRule.selectorText.match(selector)) {
            rulesToAdd.push(styleRule.cssText);
            sheetFrom.deleteRule(index);
            index -= 1;
            reconstructFrom = true;
          }
          index += 1;
        });

        // Reconstruct the innerHTML of the source section's dynamic styles
        if (reconstructFrom) {
          dynamicStylesFrom.innerHTML = Array.from(sheetFrom.cssRules)
            .map((rule) => rule.cssText)
            .join('\n');
        }

        // Add the collected rules to the target section's dynamic styles
        if (rulesToAdd.length > 0) {
          rulesToAdd.forEach((rule) => {
            sheetTo.insertRule(rule, sheetTo.cssRules.length);
          });

          // Reconstruct the innerHTML of the target section's dynamic styles
          dynamicStylesTo.innerHTML = Array.from(sheetTo.cssRules)
            .map((rule) => rule.cssText)
            .join('\n');
        }
      }
    }
  }
};

export const removeDynamicStyle = (selector: string, value: StringMap, mobile = false, forceStylesSheet: string | false = false) => {
  // We check if selector is dedicated to a widget, section, row or column
  const idWidget = selector.match(new RegExp('#spm_(?:widget|section|row|column)_([a-zA-Z0-9]+)'));
  const isWidget = Array.isArray(idWidget) && idWidget.length > 0;
  const template = getTemplateIframeDocument();

  // Remove inline styles for the targeted properties (inline styles have higher specificity)
  if (template && !mobile) {
    const elements = template.querySelectorAll(selector);
    elements.forEach((element) => {
      const htmlElement = element as HTMLElement;
      if (htmlElement.style) {
        Object.keys(value).forEach((property) => {
          if (htmlElement.style.getPropertyValue(property)) {
            htmlElement.style.removeProperty(property);
          }
        });
      }
    });
  }

  let needApplyBackgroundReferenceFromSelector = false;
  getSectionIdsFromIframe(!isWidget).forEach((sectionId) => {
    // eslint-disable-next-line radix
    if (sectionId) {
      const dynamicStyles = getDynamicStylesForActiveSection(sectionId, mobile, forceStylesSheet);
      if (dynamicStyles) {
        // Skip entire processing if mobile styles are disabled
        if (shouldSkipMobileStylesheet(dynamicStyles)) {
          return;
        }

        const { sheet } = dynamicStyles;
        if (sheet) {
          let reconstruct = false;
          const reconstructPadding: any[] = [];
          Object.keys(value).forEach((property) => {
            if (['background', 'background-color'].includes(property)) {
              needApplyBackgroundReferenceFromSelector = true;
            }
            const cssRuleSelector = property.replace(/-./g, (char) => char.toUpperCase()[1]);
            let index = 0;

            const processCssRule = (rule: CSSRule, mediaSheet?: CSSMediaRule) => {
              const styleRule = rule as CSSStyleRule;
              const currentSheet = mediaSheet || sheet;
              if (typeof styleRule.selectorText === 'string') {
                let notWidgetCondition = !isWidget && styleRule.selectorText === selector;
                if (cssRuleSelector === 'backgroundImage') {
                  // Check it because background-image is applied with multiple selectors
                  notWidgetCondition = !isWidget && !!styleRule.selectorText.match(new RegExp(selector));
                }
                const currentIdWidget = styleRule.selectorText.match(new RegExp('#spm_(?:widget|section|row|column)_([a-zA-Z0-9]+)'));
                const currentIsWidget = Array.isArray(currentIdWidget) && currentIdWidget.length > 0;

                if (
                  (
                    // eslint-disable-next-line max-len
                    (notWidgetCondition || (!isWidget && !currentIsWidget && template.querySelector(selector) && template.querySelector(selector) === template.querySelector(styleRule.selectorText)))
                    || (idWidget && isWidget && (styleRule.selectorText === selector
                      || (currentIdWidget && currentIsWidget && template.querySelector(selector) === template.querySelector(styleRule.selectorText))))
                  )
                  && (styleRule.style[cssRuleSelector as any] || (cssRuleSelector.match('padding') && styleRule.style['padding' as any]))
                ) {
                  if (cssRuleSelector === 'backgroundImage' && (styleRule.style[cssRuleSelector].match('gradient') || styleRule.style.backgroundColor.match('rgb'))) {
                    let bgColor = styleRule.style.backgroundColor;
                    if (styleRule.style[cssRuleSelector].match('gradient')) {
                      bgColor = styleRule.style[cssRuleSelector].replace(/.*((linear|radial)-gradient.*\))$/, '$1');
                    }
                    currentSheet.insertRule(`${selector} {background: ${bgColor};}`, index);
                    index += 1;
                    currentSheet.deleteRule(index);
                    index -= 1;
                    reconstruct = true;
                  } else if (styleRule.cssText.match(`${property}:`) || (styleRule.cssText.match('padding:') && cssRuleSelector.match('padding'))) {
                    currentSheet.deleteRule(index);
                    index -= 1;
                    reconstruct = true;
                    if (styleRule.cssText.match('padding:')) {
                      const paddings = ['padding-top', 'padding-right', 'padding-bottom', 'padding-left'];
                      paddings.forEach((padding: any) => {
                        if (padding !== property && styleRule.style[padding]) {
                          const paddingStyle = { [padding]: styleRule.style[padding] };
                          reconstructPadding.push(paddingStyle);
                        }
                      });
                    }
                  }
                }
              }
            };

            Array.from(sheet.cssRules).forEach((styleRule) => {
              if (styleRule.constructor.name === 'CSSStyleRule') {
                processCssRule(styleRule as CSSStyleRule);
                index += 1;
              } else if (styleRule.constructor.name === 'CSSMediaRule') {
                index = 0;
                Array.from((styleRule as CSSMediaRule).cssRules).forEach((mediaRule: CSSRule) => {
                  processCssRule(mediaRule as CSSStyleRule, styleRule as CSSMediaRule);
                  index += 1;
                });
              }
            });
          });
          if (reconstruct) {
            dynamicStyles.innerHTML = Array.from(sheet.cssRules)
              .map((rule) => rule.cssText)
              .join('\n');

            if (reconstructPadding.length) {
              reconstructPadding.forEach((padding) => {
                // eslint-disable-next-line @typescript-eslint/no-use-before-define
                addOrUpdateDynamicStyle(selector, padding, mobile);
              });
            }
          }
        }
      }
    }
  });
  if (needApplyBackgroundReferenceFromSelector) {
    const { applyBackgroundReferenceFromSelector } = useEditorBackground();
    applyBackgroundReferenceFromSelector(selector, template);
  }
};

export function extractOnlyMediaBlocks(css: string) {
  let result = '';
  let index = 0;

  while (index < css.length) {
    const mediaStart = css.indexOf('@media', index);
    if (mediaStart === -1) break;

    const openBrace = css.indexOf('{', mediaStart);
    if (openBrace === -1) break;

    let depth = 1;
    let cursor = openBrace + 1;

    while (cursor < css.length && depth > 0) {
      const char = css[cursor];
      if (char === '{') depth += 1;
      else if (char === '}') depth -= 1;
      cursor += 1;
    }

    const block = css.slice(mediaStart, cursor).trim();
    result += block;

    index = cursor;
  }

  return result.trim();
}

function shouldExtractMediaBlocks(css: string) {
  const cleaned = css.replace(/@media[^{]+\{[^{}]*\{[^{}]*\}[^}]*\}/gs, '');
  return /[^{]+\{[^}]+\}/.test(cleaned);
}

export const cleanupDynamicStyle = () => {
  if (isDisplayTemplate(state.template?.type) || isEmailTemplate(state.template?.type)) {
    const template = getTemplateIframeDocument();
    let updateSections = false;
    const cssTextExists: any[] = [];
    getSectionIdsFromIframe(false).forEach((sectionId) => {
      // eslint-disable-next-line radix
      if (sectionId) {
        const dynamicStyles = getDynamicStylesForActiveSection(sectionId);
        if (dynamicStyles) {
          // Skip entire processing if mobile styles are disabled
          if (shouldSkipMobileStylesheet(dynamicStyles)) {
            return;
          }

          const { sheet } = dynamicStyles;
          if (sheet) {
            let reconstruct = false;
            let index = 0;
            Object.values(sheet.cssRules).forEach((styleRule: any) => {
              if (typeof styleRule.selectorText === 'string') {
                if (!cssTextExists.includes(styleRule.cssText)) {
                  cssTextExists.push(styleRule.cssText);
                  // We check if selector is dedicated to a widget or not
                  const idWidget = styleRule.selectorText.match(new RegExp('#spm_widget_([a-zA-Z0-9]+)'));
                  const isWidget = Array.isArray(idWidget) && idWidget.length > 0;
                  if (isWidget) {
                    const currentSectionId = template.querySelector(`[data-spmelementid="${sectionId}"]`) as HTMLElement;
                    // Remove pseudo-classes from selector before querySelector (querySelector doesn't support pseudo-classes)
                    const pseudoClasses = '(hover|focus|active|visited|link|before|after|first-child|last-child'
                      + '|nth-child\\([^)]+\\)|not\\([^)]+\\)|first-of-type|last-of-type|nth-of-type\\([^)]+\\)'
                      + '|only-child|only-of-type|empty|disabled|enabled|checked|valid|invalid)';
                    const selectorToCheck = styleRule.selectorText.replace(new RegExp(`:${pseudoClasses}`, 'gi'), '');
                    const currentTarget = template.querySelector(selectorToCheck);
                    let deleteRule = false;
                    if (currentTarget) {
                      const targetSectionId = getSectionParent(currentTarget as HTMLElement);
                      if (currentSectionId
                          && targetSectionId
                          && currentSectionId.getAttribute('data-spmelementid') !== targetSectionId.getAttribute('data-spmelementid')
                      ) {
                        deleteRule = true;
                      }
                    } else {
                      deleteRule = true;
                    }
                    if (deleteRule) {
                      reconstruct = true;
                      sheet.deleteRule(index);
                      index -= 1;
                    }
                  }
                } else {
                  reconstruct = true;
                  sheet.deleteRule(index);
                  index -= 1;
                }
              }
              index += 1;
            });
            if (reconstruct) {
              dynamicStyles.innerHTML = Array.from(sheet.cssRules)
                .map((rule) => rule.cssText)
                .join('\n');
              updateSections = true;
            }
          }
        }

        // Deduplicate identical rules in the "dynamic_persistent" stylesheet.
        // The logic above only processes the "dynamic" sheet, so persistent rules
        // (e.g. image max-width/width set on resize) are never cleaned and identical
        // rules pile up indefinitely, bloating the saved CSS and making the backend
        // sanitize (juice/cheerio) explode. We only drop strictly identical rules.
        const persistentStyles = getDynamicStylesForActiveSection(sectionId, false, 'dynamic_persistent');
        if (persistentStyles) {
          const persistentSheet = persistentStyles.sheet;
          if (persistentSheet) {
            const persistentCssExists: string[] = [];
            let persistentReconstruct = false;
            let persistentIndex = 0;
            Object.values(persistentSheet.cssRules).forEach((styleRule: any) => {
              if (typeof styleRule.selectorText === 'string') {
                if (!persistentCssExists.includes(styleRule.cssText)) {
                  persistentCssExists.push(styleRule.cssText);
                } else {
                  persistentReconstruct = true;
                  persistentSheet.deleteRule(persistentIndex);
                  persistentIndex -= 1;
                }
              }
              persistentIndex += 1;
            });
            if (persistentReconstruct) {
              persistentStyles.innerHTML = Array.from(persistentSheet.cssRules)
                .map((rule) => rule.cssText)
                .join('\n');
              updateSections = true;
            }
          }
        }
      }
    });
    const stylesheets = template.querySelectorAll('style[data-spm-styles="dynamic_persistent_mobile"]');

    stylesheets.forEach((stylesheet) => {
      const original = stylesheet.innerHTML;

      // Skip if mobile styles are disabled (commented out)
      if (original.includes('/*data-spmhidemobile')) {
        return;
      }

      const hasMedia = /@media\s+[^{]+\{/.test(original);
      if (original && hasMedia && shouldExtractMediaBlocks(original)) {
        // eslint-disable-next-line no-param-reassign
        stylesheet.innerHTML = extractOnlyMediaBlocks(original);
      }
    });
    if (updateSections) {
      updateSectionsInState(false);
    }
  }
};

export const removeDynamicStyleWithoutPseudoClasses = (selector: string, property: Property) => {
  const { name, value } = property;
  let newSelector = selector;
  newSelector = newSelector.replace(new RegExp('(:[a-zA-Z-]+\\([-:.a-zA-Z#]+\\))+', 'g'), '');
  removeDynamicStyle(newSelector, { [name]: value });
};

const dynamicStylesCache = createTimedLRUCache<string, StringMap>({
  timeout: 1500,
  maxSize: 200,
});

export const getDynamicStylesForSelector = (selector: string, mobile = false):
  StringMap => dynamicStylesCache.getOrCompute(`${selector}|${mobile === true ? 'mobile' : 'desktop'}`, () => {
  const selectorStyles: StringMap = {};
  const idWidget = selector.match(new RegExp('#spm_widget_([a-zA-Z0-9]+)'));
  const isWidget = Array.isArray(idWidget) && idWidget.length > 0;

  const template = getTemplateIframeDocument();

  // Check if selector contains pseudo-classes
  const pseudoClassPattern = '(hover|focus|active|visited|link|before|after|first-child|last-child'
    + '|nth-child\\([^)]+\\)|not\\([^)]+\\)|first-of-type|last-of-type|nth-of-type\\([^)]+\\)'
    + '|only-child|only-of-type|empty|disabled|enabled|checked|valid|invalid)';
  const pseudoClassRegex = new RegExp(`:${pseudoClassPattern}`, 'i');
  const hasPseudoClass = pseudoClassRegex.test(selector);

  // If selector has pseudo-class, remove it for querySelector
  const pseudoClasses = pseudoClassPattern;
  const selectorWithoutPseudo = hasPseudoClass
    ? selector.replace(new RegExp(`:${pseudoClasses}`, 'gi'), '')
    : selector;
  const targetElement = template.querySelector(selectorWithoutPseudo);

  setActiveSectionFromSelector(selectorWithoutPseudo);

  const { template: templateState } = state;
  if (!templateState?.activeSection || templateState.activeSection === -1) {
    return selectorStyles;
  }

  const dynamicStylesheet = getDynamicStylesForActiveSection(null, mobile);
  const { sheet } = dynamicStylesheet || {};
  if (!sheet) {
    return selectorStyles;
  }

  const createSelectorStyles = (rule: CSSStyleRule) => {
    const { selectorText, style } = rule as CSSStyleRule;
    if (!selectorText) return;

    const currentIdWidgetMatch = selectorText.match(/#spm_widget_([a-zA-Z0-9]+)/);
    const currentIsWidget = Boolean(currentIdWidgetMatch);

    // For pseudo-classes, compare selectors textually instead of using querySelector
    const currentPseudoRegex = new RegExp(`:${pseudoClassPattern}`, 'i');
    const currentHasPseudoClass = currentPseudoRegex.test(selectorText);
    const currentSelectorWithoutPseudo = currentHasPseudoClass
      ? selectorText.replace(new RegExp(`:${pseudoClasses}`, 'gi'), '')
      : selectorText;
    const currentElement = template.querySelector(currentSelectorWithoutPseudo);

    // For exact match, compare full selectors (including pseudo-classes)
    const isExactMatch = selectorText === selector;

    // Extract pseudo-class from both selectors to compare them
    const extractPseudoClass = (sel: string) => {
      const pseudoPattern = '(hover|focus|active|visited|link|before|after|first-child|last-child'
        + '|nth-child\\([^)]+\\)|not\\([^)]+\\)|first-of-type|last-of-type|nth-of-type\\([^)]+\\)'
        + '|only-child|only-of-type|empty|disabled|enabled|checked|valid|invalid)';
      const match = sel.match(new RegExp(`:${pseudoPattern}`, 'i'));
      return match ? match[0] : null;
    };

    // If requested selector has pseudo-class, match if same element AND same pseudo-class
    // Otherwise, match same element even if selectors differ slightly
    let shouldMatch = false;
    if (hasPseudoClass) {
      // Selector has :hover, :focus, etc.
      // Match if: exact selector OR (same element AND same pseudo-class)
      const requestedPseudoClass = extractPseudoClass(selector);
      const rulePseudoClass = extractPseudoClass(selectorText);
      const samePseudoClass = requestedPseudoClass === rulePseudoClass && requestedPseudoClass !== null;
      const isSameElement = !isWidget && currentElement === targetElement && targetElement !== null;
      const isWidgetMatch = isWidget && currentIsWidget && currentElement === targetElement && targetElement !== null;
      shouldMatch = isExactMatch || (samePseudoClass && (isSameElement || isWidgetMatch));
    } else {
      // Normal selector -> match by element or exact selector
      const isSameElement = !isWidget && currentElement === targetElement && targetElement !== null;
      const isWidgetMatch = isWidget && (selectorText === selector || (currentIsWidget && currentElement === targetElement && targetElement !== null));
      shouldMatch = isExactMatch || isSameElement || isWidgetMatch;
    }

    if (shouldMatch) {
      Array.from({ length: style.length }).forEach((_, i) => {
        const cssProp = style[i];
        const value = style.getPropertyValue(cssProp);
        if (value) {
          selectorStyles[cssProp] = value;
        }
      });
    }
  };

  Array.from(sheet.cssRules)
    .forEach((rule) => {
      if (rule.constructor.name === 'CSSStyleRule') {
        createSelectorStyles(rule as CSSStyleRule);
      } else if (rule.constructor.name === 'CSSMediaRule') {
        Array.from((rule as CSSMediaRule).cssRules).forEach((mediaRule: CSSRule) => {
          if (mediaRule.constructor.name === 'CSSStyleRule') {
            createSelectorStyles(mediaRule as CSSStyleRule);
          }
        });
      }
    });
  return selectorStyles;
});

export const addOrUpdateDynamicStyle = (selector: string, value: StringMap, mobile = false, forceStylesSheet: string | false = false) => {
  removeDynamicStyle(selector, value, mobile, forceStylesSheet);
  addNewDynamicStyle(selector, value, mobile, forceStylesSheet);
};

export const getChildrenType = (parentType: TemplateStructureEnum, templateType: TemplateParentTypeEnum): TemplateStructureEnum => {
  switch (parentType) {
    case TemplateStructureEnum.PAGE: return templateType === TemplateParentTypeEnum.EMAIL ? TemplateStructureEnum.SECTION : TemplateStructureEnum.LINE;
    case TemplateStructureEnum.SECTION: return TemplateStructureEnum.LINE;
    case TemplateStructureEnum.LINE: return TemplateStructureEnum.COLUMN;
    default: return TemplateStructureEnum.WIDGET;
  }
};

export const getChildrenStructure = (parentId: string, childrenType: string): Array<HTMLElement> => {
  let childrenStructures: Array<HTMLElement> = [];
  const parentIdentifier = parentId.indexOf('#') !== -1 ? parentId : `#${parentId}`;
  const template = getTemplateIframeDocument();
  const parentStructure = template.querySelector(parentIdentifier);
  if (parentStructure) {
    childrenStructures = Array.from(parentStructure.querySelectorAll(TEMPLATE_STRUCTURE_MAP[childrenType]));
  }
  return childrenStructures;
};

export const getParentClosestStructure = (elementId: string, structure: string): string|null => {
  const template = getTemplateIframeDocument();
  const element = template?.querySelector(`#${elementId}`) as HTMLElement;
  if (element) {
    const parent = (element.closest(TEMPLATE_STRUCTURE_MAP[structure]) as HTMLElement);
    if (parent) {
      if (!parent.id) { // Generate id if not exists
        const parentId = `${TEMPLATE_ITEM_IDENTIFIER_PREFIX_MAP[structure]}_${generateUniqStructureId()}`;
        parent.setAttribute('id', parentId);
      }
      return parent.id;
    }
  }
  return null;
};

export const addValuesToProperties = (
  selector: string,
  properties: Array<Property>,
  fieldGroupSelector: string,
  structureId: string,
  parentColumnId: string,
  parentLineId: string,
): Array<Property> => properties.map((property: Property) => {
  const rawSelector = property.customSelector ?? selector;

  let resolvedSelector = replaceSelectors(rawSelector, '{SELECTOR}', fieldGroupSelector);
  resolvedSelector = replaceSelectors(resolvedSelector, '{ID}', structureId);
  resolvedSelector = replaceSelectors(resolvedSelector, '{COLUMN_ID}', parentColumnId);
  resolvedSelector = replaceSelectors(resolvedSelector, '{LINE_ID}', parentLineId);

  if (property.customSelector) {
    // eslint-disable-next-line no-param-reassign
    property.customSelector = resolvedSelector;
  }
  // eslint-disable-next-line no-param-reassign
  property.value = property.getter(resolvedSelector, property.name, structureId);
  return property;
});

export const computeActiveSection = (type: string, identifier: string): string | number => {
  if (state.template) {
    if (state.template.type !== 'email') return state.template.content.sections[0]; // template is not email => only one section
    if (type === TemplateStructureEnum.PAGE) return state.template.content.design; // click on global style button => design section

    const template = getTemplateIframeDocument();
    const structureId = getParentClosestStructure(identifier, 'section'); // get the parent section identifier
    if (structureId) {
      const elementId = (template?.querySelector(`#${structureId}`) as HTMLElement).getAttribute('data-spmelementid'); // get the section id
      if (elementId) {
        return asInt(elementId);
      }
    }
  }
  return -1;
};
